<template>
  <v-card style="height: 100%">
    <v-card-title>{{ $t("profile.notifications.title") }}</v-card-title>
    <v-card-text>
      <div
        v-for="(alertItem, index) in alerts"
        :key="index"
        @click="alertItem.onClick && alertItem.onClick()"
      >
        <v-hover v-if="alertItem.onClick" v-slot="{ hover }">
          <v-alert
            dense
            :outlined="!hover"
            :icon="alertItem.icon"
            :type="alertItem.type"
            class="safety-elevation-3 alert-hover"
          >
            {{ alertItem.text }}
          </v-alert>
        </v-hover>

        <v-alert
          v-else
          dense
          outlined
          :icon="alertItem.icon"
          :type="alertItem.type"
          class="safety-elevation-3"
        >
          {{ alertItem.text }}
          <v-btn
            v-if="alertItem.button"
            small
            class="primary ml-2"
            :to="alertItem.button.to"
            @click="alertItem.button.onClick && alertItem.button.onClick()"
          >
            {{ alertItem.button.text }}
          </v-btn>
        </v-alert>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapState } from "vuex";

export default {
  props: ["employee", "riskAnalyses"],
  computed: {
    ...mapState(["me", "appSettings"]),
    unsignedRiskAnalyses() {
      if (!this.riskAnalyses || !this.riskAnalyses.length) return 0;
      return this.riskAnalyses.filter(
        (item) => item.riskAnalysis.status === "confirmed" && !item.signature,
      ).length;
    },
    alerts() {
      const alerts = [];

      if (this.employee.modes && this.employee.modes.length) {
        const modeNames = this.employee.modes
          .map((mode) => mode.name)
          .join(", ");
        alerts.push({
          icon: "error",
          type: "error",
          text: `${this.$t("employees.alert.modes")}: ${modeNames}`,
        });
      }

      if (this.employee.overdueCount) {
        alerts.push({
          icon: "error",
          type: "error",
          text: this.$tc(
            "employees.alert.overdueSignatures",
            this.employee.overdueCount,
          ),
          onClick: () => this.$vuetify.goTo("#profileInstructions"),
        });
      } else if (this.employee.pendingCount) {
        alerts.push({
          icon: "warning",
          type: "warning",
          text: this.$tc(
            "employees.alert.pendingSignatures",
            this.employee.pendingCount,
          ),
          onClick: () => this.$vuetify.goTo("#profileInstructions"),
        });
      }

      if (this.employee.unconfirmedCount) {
        alerts.push({
          icon: "warning",
          type: "warning",
          text: this.$tc(
            "employees.alert.pendingConfirmations",
            this.employee.unconfirmedCount,
          ),
          onClick: () => this.$vuetify.goTo("#profileInstructions"),
        });
      }

      if (
        this.me &&
        this.employee.id !== this.me.id &&
        this.employee.missingParentTags &&
        this.employee.missingParentTags.length
      ) {
        const tagNames = this.employee.missingParentTags
          .map((tag) => tag.name)
          .join(", ");
        alerts.push({
          icon: "warning",
          type: "warning",
          text: `${this.$t("tags.missingParentTags")} ${tagNames}`,
        });
      }

      if (
        this.employee.activeMentorships &&
        this.employee.activeMentorships.length
      ) {
        alerts.push({
          icon: "warning",
          type: "warning",
          text: this.$tc(
            "mentorship.actions.alert.hasActiveMentorships",
            this.employee.activeMentorships.length,
          ),
          button: {
            text: this.$t("dialog.open"),
            to:
              this.me && this.me.id === this.employee.id
                ? "/mentorship"
                : `/mentorship/employees/${this.employee.id}`,
          },
        });
      }

      if (
        this.employee.activeMentorshipActions &&
        this.employee.activeMentorshipActions.length
      ) {
        alerts.push({
          icon: "warning",
          type: "warning",
          text: this.$tc(
            "mentorship.actions.alert.hasActiveMentorshipActions",
            this.employee.activeMentorshipActions.length,
          ),
          button: {
            text: this.$t("dialog.open"),
            to: {
              path:
                this.me && this.me.id === this.employee.id
                  ? "/mentorship"
                  : `/mentorship/employees/${this.employee.id}`,
              query: { view: "actions" },
            },
          },
        });
      }

      if (this.employee.riskAnalysisPendingActions) {
        alerts.push({
          icon: "warning",
          type: "warning",
          text: this.$tc(
            "riskAnalysis.alert.hasRiskAnalysisPendingActions",
            this.employee.riskAnalysisPendingActions,
          ),
          button: {
            text: this.$t("dialog.open"),
            onClick: () => this.toV3("/risk-analysis"),
          },
        });
      }

      if (
        this.appSettings.isRiskAnalysisSigningEnabled &&
        this.unsignedRiskAnalyses
      ) {
        alerts.push({
          icon: "warning",
          type: "warning",
          text: this.$tc(
            "riskAnalysis.alert.hasUnsignedRiskAnalyses",
            this.unsignedRiskAnalyses,
          ),
          onClick: () => this.$vuetify.goTo("#profileRiskAnalyses"),
        });
      }

      if (this.employee.recalledToolsCount) {
        alerts.push({
          icon: "error",
          type: "error",
          text: this.$tc(
            "tools.alert.hasRecalledTools",
            this.employee.recalledToolsCount,
            { count: this.employee.recalledToolsCount },
          ),
          onClick: () => this.$vuetify.goTo("#profileTools"),
        });
      }

      return alerts;
    },
  },
  methods: {
    toV3(path) {
      window.location.replace("/v3" + path);
    },
  },
};
</script>

<style scoped>
.alert-hover:hover {
  border: 1px solid rgba(0, 0, 0, 0) !important;
  cursor: pointer;
}
</style>
